import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../shared/utils/api';

const PERIODS = [
  { key: 'day',   label: 'Day' },
  { key: 'week',  label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'all',   label: 'All time' },
];

const MODULES = [
  { key: 'FLK1', label: 'FLK 1' },
  { key: 'FLK2', label: 'FLK 2' },
  { key: '',     label: 'Both' },
];

function cellClass(cell) {
  if (!cell || cell.attempted < 5) return '';
  if (cell.accuracy_pct < 50) return 'perf-cell-red';
  if (cell.accuracy_pct <= 70) return 'perf-cell-amber';
  return 'perf-cell-green';
}

function cellText(cell) {
  if (!cell || cell.attempted === 0) return '—';
  if (cell.attempted < 5) return `(${cell.attempted})`;
  return `${cell.accuracy_pct}% (${cell.attempted})`;
}

function isEmptyCell(cell) {
  return !cell || cell.attempted === 0;
}

export default function PerformancePage() {
  const navigate = useNavigate();

  const [period, setPeriod] = useState('week');
  const [module, setModule] = useState('');
  const [offset, setOffset] = useState(0);
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // expandedL1 keyed by subject name; expandedL2 keyed by composite "subjectName||generalTopic"
  const [expandedL1, setExpandedL1] = useState(new Set());
  const [expandedL2, setExpandedL2] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  const loadGrid = useCallback((p, m, o) => {
    setLoading(true);
    setError(false);
    const moduleParam = m ? `&module=${m}` : '';
    api(`/api/questions/stats/grid?question_type=drill&period=${p}&offset=${o}${moduleParam}`)
      .then((d) => { setGridData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  useEffect(() => {
    setOffset(0);
    setExpandedL1(new Set());
    setExpandedL2(new Set());
    setAllExpanded(false);
  }, [period, module]);

  useEffect(() => { loadGrid(period, module, offset); }, [period, module, offset, loadGrid]);

  const handlePeriod = (p) => setPeriod(p);
  const handleModule = (m) => setModule(m);
  const handleEarlier = () => setOffset((o) => o + 1);
  const handleLater = () => setOffset((o) => Math.max(0, o - 1));

  const toggleL1 = (name) => {
    setExpandedL1((prev) => {
      const next = new Set(prev);
      if (next.has(name)) { next.delete(name); } else { next.add(name); }
      return next;
    });
  };

  const toggleL2 = (l1Name, l2Name) => {
    const key = `${l1Name}||${l2Name}`;
    setExpandedL2((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  const toggleAllDetail = () => {
    if (allExpanded) {
      setExpandedL1(new Set());
      setExpandedL2(new Set());
      setAllExpanded(false);
    } else {
      const l1Names = new Set(
        (gridData?.rows || []).filter((r) => r.level === 'subject').map((r) => r.name)
      );
      const l2Keys = new Set();
      let currentL1 = null;
      for (const r of (gridData?.rows || [])) {
        if (r.level === 'subject') currentL1 = r.name;
        else if (r.level === 'study_unit' && currentL1) l2Keys.add(`${currentL1}||${r.general_topic}`);
      }
      setExpandedL1(l1Names);
      setExpandedL2(l2Keys);
      setAllExpanded(true);
    }
  };

  // Sync allExpanded flag when manually expanding/collapsing
  useEffect(() => {
    if (!gridData) return;
    const l1Count = (gridData.rows || []).filter((r) => r.level === 'subject').length;
    const l2Count = (gridData.rows || []).filter((r) => r.level === 'study_unit').length;
    setAllExpanded(expandedL1.size === l1Count && expandedL2.size === l2Count && l1Count > 0);
  }, [expandedL1, expandedL2, gridData]);

  // Build visible rows from flat list
  const visibleRows = useMemo(() => {
    if (!gridData?.rows) return [];
    const result = [];
    let currentL1 = null;
    let currentL2 = null;
    for (const row of gridData.rows) {
      if (row.level === 'overall') {
        result.push(row);
      } else if (row.level === 'subject') {
        currentL1 = row.name;
        result.push(row);
      } else if (row.level === 'study_unit') {
        currentL2 = row.general_topic;
        if (expandedL1.has(currentL1)) result.push(row);
      } else if (row.level === 'subtopic') {
        const l2Key = `${currentL1}||${currentL2}`;
        if (expandedL1.has(currentL1) && expandedL2.has(l2Key)) result.push(row);
      }
    }
    return result;
  }, [gridData, expandedL1, expandedL2]);

  const hasAttempts = gridData?.rows?.some(
    (r) => r.level === 'overall' && r.cells?.some((c) => c && c.attempted > 0)
  );

  if (!loading && !error && gridData && !hasAttempts) {
    return (
      <div className="w-full px-6 py-sp-6">
        <h1 className="text-2xl font-bold text-text-primary mb-sp-5">Performance</h1>
        <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-6 py-sp-10 text-center">
          <p className="text-sm text-text-secondary mb-sp-4">No drills completed yet.</p>
          <button
            type="button"
            onClick={() => navigate('/drills-app/practice')}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
          >
            Start your first drill session
          </button>
        </div>
      </div>
    );
  }

  const buckets = gridData?.buckets || [];
  const showNav = period !== 'all';

  const moduleLabel = module === 'FLK1' ? 'FLK 1' : module === 'FLK2' ? 'FLK 2' : 'FLK 1 & 2';
  const firstColLabel = {
    week:  `SQE 1 — ${moduleLabel} · Week ending`,
    day:   `SQE 1 — ${moduleLabel} · Day`,
    month: `SQE 1 — ${moduleLabel} · Month`,
    all:   `SQE 1 — ${moduleLabel} · All time`,
  }[period] || 'Topic';

  return (
    <div className="w-full px-6 py-sp-6">
      <h1 className="text-2xl font-bold text-text-primary mb-sp-5">Performance</h1>

      <div className="perf-grid-controls">
        <span className="perf-label">PERIOD</span>
        {PERIODS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`perf-period-chip${period === key ? ' active' : ''}`}
            onClick={() => handlePeriod(key)}
          >
            {label}
          </button>
        ))}

        <span className="perf-label">PAPER</span>
        {MODULES.map(({ key, label }) => (
          <button
            key={key === '' ? 'both' : key}
            type="button"
            className={`perf-period-chip${module === key ? ' active' : ''}`}
            onClick={() => handleModule(key)}
          >
            {label}
          </button>
        ))}

        {showNav && (
          <>
            <button type="button" className="perf-nav-link" onClick={handleEarlier}>
              ‹ Earlier dates
            </button>
            <button
              type="button"
              className="perf-nav-link"
              onClick={handleLater}
              disabled={offset === 0}
            >
              Later dates ›
            </button>
          </>
        )}

        <button
          type="button"
          className="perf-toggle-link"
          onClick={toggleAllDetail}
        >
          {allExpanded ? 'Collapse rows' : 'Expand rows'}
        </button>
      </div>

      {loading && <p className="text-sm text-text-muted">Loading...</p>}

      {error && <p className="text-sm text-sig-red">Failed to load data. Please try again.</p>}

      {!loading && !error && gridData && (
        <div className="perf-grid-wrap">
          <table className="perf-grid-table" aria-label="Performance grid">
            <thead>
              <tr className="perf-grid-header">
                <th className="perf-name-col">{firstColLabel}</th>
                {buckets.map((b, i) => (
                  <th key={i}>{b.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, idx) => {
                const rowClass = {
                  overall:    'perf-row-overall',
                  subject:    'perf-row-l1',
                  study_unit: 'perf-row-l2',
                  subtopic:   'perf-row-l3',
                }[row.level] || '';

                const isExpandable = row.level === 'subject' || row.level === 'study_unit';
                const isOpen = row.level === 'subject'
                  ? expandedL1.has(row.name)
                  : row.level === 'study_unit'
                  ? expandedL2.has(`${row.subject_area || row.name}||${row.general_topic}`)
                  : false;

                const handleNameClick = isExpandable
                  ? () => row.level === 'subject'
                    ? toggleL1(row.name)
                    : toggleL2(row.subject_area || row.name, row.general_topic)
                  : undefined;

                return (
                  <tr key={idx} className={`perf-grid-row ${rowClass}`}>
                    <td
                      className="perf-name-cell"
                      onClick={handleNameClick}
                      title={row.name}
                    >
                      {isExpandable && (
                        <span className={`perf-expand-icon${isOpen ? ' open' : ''}`}>▶</span>
                      )}
                      {row.name}
                    </td>
                    {(row.cells || []).map((cell, ci) => (
                      <td
                        key={ci}
                        className={`perf-data-cell ${isEmptyCell(cell) ? 'perf-cell-empty' : cellClass(cell)}`}
                      >
                        {cellText(cell)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
